import axiosInstance from "../common/axiosInstance";

const assetChecklistsBaseUrl = "/api/asset-checklists";
const assessmentsBaseUrl = "/api/inspection-assessments";

// ============ Interfaces ============

export interface ChecklistItem {
  id: string;
  question: string;
  type: "boolean" | "numeric" | "text" | "selection";
  required: boolean;
  options?: string[];
  unit?: string;
  minValue?: number;
  maxValue?: number;
  category?: string;
  order?: number;
  helpText?: string;
}

export interface AssetChecklist {
  _id?: string;
  id?: string;
  name: string;
  description: string;
  category: string;
  equipmentInventoryId: string;
  equipmentSubCategoryId?: string;
  assetType: string; // Now required
  items: ChecklistItem[];
  isActive: boolean;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt?: string;
  tenantId: string;
  // Display fields (populated from backend)
  equipmentInventoryName?: string;
  equipmentSubCategoryName?: string;
}

export interface ChecklistResponse {
  itemId: string;
  value: string | number | boolean;
  status: "pass" | "fail" | "attention";
  comment?: string;
  photos?: string[];
}

export interface InspectionAssessment {
  _id?: string;
  id?: string;
  assetId: string;
  assetChecklistId: string;
  inspectorName: string;
  attachments: string[];
  inspectorEmail: string;
  inspectorId?: string;
  inspectionDate: string;
  inspectionType: "routine" | "maintenance" | "emergency" | "compliance";
  responses: ChecklistResponse[];
  overallStatus: "pass" | "fail" | "attention";
  overallComments?: string;
  correctiveActions?: string;
  score?: number;
  passedItems: number;
  failedItems: number;
  totalItems: number;
  nextInspectionDate: string; // Now required
  requiresFollowUp: boolean;
  followUpCompleted: boolean;
  // Asset-specific fields (Fire Extinguisher fields - using existing DB field names)
  hptTestDate?: string;
  nextServiceDueDate?: string;
  lastRefillDate?: string;
  nextRefillDueDate?: string;
  amcStatus?: "active" | "inactive" | "expired" | "pending" | "not-applicable";
  amcStartDate?: string;
  amcEndDate?: string;
  amcVendor?: string;
  isDraft: boolean;
  status: "draft" | "submitted" | "reviewed" | "approved" | "rejected";
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt?: string;
  tenantId: string;
  checklistDetails?: AssetChecklist;
}

// ============ Asset Inspection Service ============

class AssetInspectionService {
  // Asset Checklist CRUD
  async createAssetChecklist(
    data: Partial<AssetChecklist>
  ): Promise<AssetChecklist> {
    try {
      const response = await axiosInstance.post(assetChecklistsBaseUrl, data);
      return response.data.data;
    } catch (error) {
      console.error("Error creating asset checklist:", error);
      throw error;
    }
  }

  async getAssetChecklists(params?: {
    page?: number;
    limit?: number;
    category?: string;
    equipmentInventoryId?: string;
    equipmentSubCategoryId?: string;
    assetType?: string;
    isActive?: boolean;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{
    checklists: AssetChecklist[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    try {
      const response = await axiosInstance.get(assetChecklistsBaseUrl, {
        params,
      });
      return response.data.data;
    } catch (error) {
      console.error("Error fetching asset checklists:", error);
      throw error;
    }
  }

  async getAssetChecklistById(id: string): Promise<AssetChecklist> {
    try {
      const response = await axiosInstance.get(
        `${assetChecklistsBaseUrl}/${id}`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching asset checklist:", error);
      throw error;
    }
  }

  async updateAssetChecklist(
    id: string,
    data: Partial<AssetChecklist>
  ): Promise<AssetChecklist> {
    try {
      const response = await axiosInstance.put(
        `${assetChecklistsBaseUrl}/${id}`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("Error updating asset checklist:", error);
      throw error;
    }
  }

  async deleteAssetChecklist(id: string): Promise<void> {
    try {
      await axiosInstance.delete(`${assetChecklistsBaseUrl}/${id}`);
    } catch (error) {
      console.error("Error deleting asset checklist:", error);
      throw error;
    }
  }

  async suggestAssetChecklists(
    equipmentInventoryId: string,
    equipmentSubCategoryId?: string,
    assetType?: string
  ): Promise<AssetChecklist[]> {
    try {
      const response = await axiosInstance.get(
        `${assetChecklistsBaseUrl}/suggest`,
        {
          params: { equipmentInventoryId, equipmentSubCategoryId, assetType },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error suggesting asset checklists:", error);
      throw error;
    }
  }

  // Duplicate functionality not needed
  // async duplicateAssetChecklist(id: string, newName?: string, createdBy?: string): Promise<AssetChecklist> {
  //   try {
  //     const response = await axiosInstance.post(`${assetChecklistsBaseUrl}/${id}/duplicate`, {
  //       newName,
  //       createdBy
  //     });
  //     return response.data.data;
  //   } catch (error) {
  //     console.error('Error duplicating asset checklist:', error);
  //     throw error;
  //   }
  // }

  async toggleAssetChecklistStatus(id: string): Promise<{ isActive: boolean }> {
    try {
      const response = await axiosInstance.put(
        `${assetChecklistsBaseUrl}/${id}/toggle-status`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error toggling asset checklist status:", error);
      throw error;
    }
  }

  async getAssetChecklistStats(id: string): Promise<any> {
    try {
      const response = await axiosInstance.get(
        `${assetChecklistsBaseUrl}/${id}/stats`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching asset checklist stats:", error);
      throw error;
    }
  }

  // Inspection Assessment CRUD
  async createAssessment(
    data: Partial<InspectionAssessment>
  ): Promise<InspectionAssessment> {
    try {
      const response = await axiosInstance.post(assessmentsBaseUrl, data);
      return response.data.data;
    } catch (error) {
      console.error("Error creating inspection assessment:", error);
      throw error;
    }
  }

  async getAssessments(params?: {
    page?: number;
    limit?: number;
    assetId?: string;
    assetChecklistId?: string;
    status?: string;
    overallStatus?: string;
    inspectionType?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{
    assessments: InspectionAssessment[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    try {
      const response = await axiosInstance.get(assessmentsBaseUrl, { params });
      return response.data.data;
    } catch (error) {
      console.error("Error fetching inspection assessments:", error);
      throw error;
    }
  }

  async getAssessmentById(id: string): Promise<InspectionAssessment> {
    try {
      const response = await axiosInstance.get(`${assessmentsBaseUrl}/${id}`);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching inspection assessment:", error);
      throw error;
    }
  }

  async updateAssessment(
    id: string,
    data: Partial<InspectionAssessment>
  ): Promise<InspectionAssessment> {
    try {
      const response = await axiosInstance.put(
        `${assessmentsBaseUrl}/${id}`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("Error updating inspection assessment:", error);
      throw error;
    }
  }

  async deleteAssessment(id: string): Promise<void> {
    try {
      await axiosInstance.delete(`${assessmentsBaseUrl}/${id}`);
    } catch (error) {
      console.error("Error deleting inspection assessment:", error);
      throw error;
    }
  }

  async getAssetAssessments(
    assetId: string,
    params?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    }
  ): Promise<{
    assessments: InspectionAssessment[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    try {
      const response = await axiosInstance.get(
        `${assessmentsBaseUrl}/asset/${assetId}`,
        { params }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching asset assessments:", error);
      throw error;
    }
  }

  async submitDraft(id: string): Promise<InspectionAssessment> {
    try {
      const response = await axiosInstance.put(
        `${assessmentsBaseUrl}/${id}/submit`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error submitting draft assessment:", error);
      throw error;
    }
  }

  async reviewAssessment(
    id: string,
    status: "reviewed" | "approved" | "rejected",
    reviewNotes?: string,
    reviewedBy?: string
  ): Promise<InspectionAssessment> {
    try {
      const response = await axiosInstance.put(
        `${assessmentsBaseUrl}/${id}/review`,
        {
          status,
          reviewNotes,
          reviewedBy,
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error reviewing assessment:", error);
      throw error;
    }
  }

  async getDashboardStats(startDate?: string, endDate?: string): Promise<any> {
    try {
      const response = await axiosInstance.get(
        `${assessmentsBaseUrl}/dashboard/stats`,
        {
          params: { startDate, endDate },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      throw error;
    }
  }
}

export default new AssetInspectionService();
